import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '../../services/auth.service';
import { UserService, UserDetails } from '../../services/user.service';
import { AuthUser } from '../../interfaces/auth.interface';
import { UserProfileModalComponent } from '../user-profile-modal/user-profile-modal.component';
import { FormRegisNewMemberComponent } from '../form-regis-new-member/form-regis-new-member.component';
import { FormRenewMemberComponent } from '../form-renew-member/form-renew-member.component';
import { FormRegisAppraiserComponent } from '../form-regis-appraiser/form-regis-appraiser.component';
import { FormRenewAppraiserComponent } from '../form-renew-appraiser/form-renew-appraiser.component';
import { environment } from '../../../environments/environment';
import { formatMemberExpiry, isLifetimeMemberType, LIFETIME_MEMBER_LABEL } from '../../utils/membership.util';

@Component({
  selector: 'app-user-dashboard',
  standalone: true,
  imports: [CommonModule, UserProfileModalComponent],
  templateUrl: './user-dashboard.component.html',
  styleUrl: './user-dashboard.component.scss'
})
export class UserDashboardComponent implements OnInit, OnDestroy {
  currentUser: AuthUser | null = null;
  userDetails: UserDetails | null = null;
  isLoading = true;
  error: string | null = null;

  // Dashboard metrics
  cpdProgress = 0;
  requiredCpdHours = 24;
  currentYear = new Date().getFullYear();
  vatCpdFromApi = 0;
  isLoadingCpd = false;
  totalCourseCount = 0; // Total count of all user courses

  // License status
  vatExpiringDays: number | null = null;
  valuerExpiringDays: number | null = null;

  // VAT Member Card data
  vatMemberCardNumber: string | null = null;
  vatMemberCardExpiry: string | null = null;
  vatType: string | null = null;
  isLoadingVatCard = false;

  // Valuer Card data
  valuerCardNumber: string | null = null;
  valuerCardExpiry: string | null = null;
  valuerType: string | null = null;
  isLoadingValuerCard = false;

  // User Status
  userStatusName: string | null = null;
  userStatusId: number | null = null;

  // Company data
  companyName: string | null = null;

  // Active Courses data
  activeCourses: any[] = [];
  isLoadingActiveCourses = false;

  // CPD Hours records for checking registered courses
  cpdHoursRecords: any[] = [];

  // Active Exams data
  activeExams: any[] = [];
  isLoadingActiveExams = false;

  // Tab state for courses/exams section
  activeTab: 'courses' | 'exams' = 'courses';

  // Member Requests data
  memberRequests: any[] = [];
  isLoadingMemberRequests = false;

  // Member Renewal Requests data
  memberRenewalRequests: any[] = [];
  isLoadingMemberRenewalRequests = false;

  // Appraiser Requests data
  appraiserRequests: any[] = [];
  isLoadingAppraiserRequests = false;

  // Appraiser Renewal Requests data
  appraiserRenewalRequests: any[] = [];
  isLoadingAppraiserRenewalRequests = false;

  // User Exams data
  userExamsCount = 0;
  isLoadingUserExams = false;
  registeredExamIds: string[] = []; // examId ที่ user สมัครไปแล้ว (กันสมัครซ้ำ)

  // Modal state
  showProfileModal = false;

  // Payment slip upload
  uploadingSlips: { [requestId: string]: boolean } = {};
  uploadedSlips: { [requestId: string]: string } = {};

  // Member request: receipt type, transfer datetime, file selection tracking
  selectedMemberPaymentSlips: Map<string, { file: File; fileName: string }> = new Map();
  memberReceiptTypes: Map<string, string> = new Map();
  memberTransferDateTimes: Map<string, string> = new Map();

  // Member renewal: receipt type, transfer datetime, file selection tracking
  selectedRenewalPaymentSlips: Map<string, { file: File; fileName: string }> = new Map();
  renewalReceiptTypes: Map<string, string> = new Map();
  renewalTransferDateTimes: Map<string, string> = new Map();

  // Appraiser registration: receipt type, transfer datetime, file selection tracking
  selectedAppraiserPaymentSlips: Map<string, { file: File; fileName: string }> = new Map();
  appraiserReceiptTypes: Map<string, string> = new Map();
  appraiserTransferDateTimes: Map<string, string> = new Map();

  // Appraiser renewal: receipt type, transfer datetime, file selection tracking
  selectedAppraiserRenewalPaymentSlips: Map<string, { file: File; fileName: string }> = new Map();
  appraiserRenewalReceiptTypes: Map<string, string> = new Map();
  appraiserRenewalTransferDateTimes: Map<string, string> = new Map();

  private userSubscription: Subscription | undefined;

  constructor(
    private authService: AuthService,
    private userService: UserService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private modalService: NgbModal,
    private http: HttpClient
  ) { }

  ngOnInit(): void {
    console.log('🚀 UserDashboard: Component initialized');
    this.loadUserData();
    this.loadVatCpdHours();
    this.loadActiveCourses();
    this.loadActiveExams();
    this.loadMemberRequests();
    this.loadMemberRenewalRequests();
    this.loadAppraiserRequests();
    this.loadAppraiserRenewalRequests();
    this.loadUserExams();
  }

  ngOnDestroy(): void {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
  }

  private loadUserData(): void {
    this.isLoading = true;
    this.error = null;

    // Subscribe to current user from AuthService
    this.userSubscription = this.authService.currentUser$.subscribe({
      next: (user) => {
        if (user) {
          console.log('✅ User authenticated:', user.cid);
          this.currentUser = user;

          // Check if user information exists before loading details
          this.checkUserInformation(user.id);
        } else {
          console.log('❌ No authenticated user');
          this.isLoading = false;
          this.error = 'กรุณาเข้าสู่ระบบ';
        }
      },
      error: (error) => {
        console.error('❌ Error in user subscription:', error);
        this.isLoading = false;
        this.error = 'เกิดข้อผิดพลาดในการโหลดข้อมูลผู้ใช้';
      }
    });
  }

  /**
   * Check if user information exists before loading dashboard
   * If 404, redirect to register page
   */
  private checkUserInformation(userId: string): void {
    console.log('🔍 Checking user information for ID:', userId);
    console.log('🔍 API URL:', `${environment.apiUrl}/user-information/user/${userId}`);

    this.http.get(`${environment.apiUrl}/user-information/user/${userId}`).subscribe({
      next: () => {
        console.log('✅ User information exists, loading dashboard data');
        this.loadUserDetails(userId);
        this.loadVatMemberCard(userId);
      },
      error: (error) => {
        console.log('❌ Error response:', error);
        console.log('❌ Error status:', error.status);

        if (error.status === 404) {
          console.log('⚠️ User information not found (404) - Redirecting to register');
          console.log('🔄 Attempting navigation to /register...');
          this.isLoading = false;

          // Use setTimeout to ensure navigation happens after current cycle
          setTimeout(() => {
            console.log('🚀 Executing navigation to /register');
            this.router.navigate(['/register']).then(
              (success) => console.log('✅ Navigation successful:', success),
              (error) => console.error('❌ Navigation failed:', error)
            );
          }, 100);
        } else {
          console.error('❌ Error checking user information:', error);
          // For other errors, try to load user details anyway
          this.loadUserDetails(userId);
        }
      }
    });
  }

  private loadUserDetails(userId: string): void {
    console.log('🌐 Loading user details for ID:', userId);
    console.log('📡 API URL:', `${environment.apiUrl}/users/${userId}/full-details`);

    this.userService.getUserById(userId).subscribe({
      next: (details) => {
        console.log('✅ User details loaded successfully:', details);
        console.log('✅ Details type:', typeof details);
        console.log('✅ Details is truthy:', !!details);

        if (details) {
          this.userDetails = details;
          this.error = null;
          this.calculateMetrics();
          this.checkLicenseStatus();
          console.log('✅ All data processed, setting isLoading to false');
          console.log('✅ userDetails assigned:', !!this.userDetails);
        } else {
          console.warn('⚠️ User details is null or undefined');
          this.error = 'ไม่พบข้อมูลผู้ใช้งาน';
        }

        this.isLoading = false;
        console.log('✅ isLoading set to:', this.isLoading);
        console.log('✅ Final state - isLoading:', this.isLoading, 'error:', this.error, 'userDetails:', !!this.userDetails);

        // Force change detection
        this.cdr.detectChanges();
        console.log('✅ Change detection triggered');
      },
      error: (error) => {
        console.error('❌ Error loading user details:', error);
        this.isLoading = false;

        if (error.status === 404) {
          this.error = 'ไม่พบข้อมูลผู้ใช้งาน กรุณาติดต่อผู้ดูแลระบบ';
        } else if (error.status === 0) {
          this.error = `ไม่สามารถเชื่อมต่อ server ได้ กรุณาตรวจสอบว่า Backend server ทำงานอยู่ที่ ${environment.apiUrl}`;
        } else if (error.status === 500) {
          this.error = 'เกิดข้อผิดพลาดในเซิร์ฟเวอร์ กรุณาติดต่อผู้ดูแลระบบ';
        } else {
          this.error = `เกิดข้อผิดพลาดในการโหลดข้อมูล (${error.status})`;
        }

        console.log('❌ Error occurred, isLoading set to:', this.isLoading);

        // Force change detection
        this.cdr.detectChanges();
      }
    });
  }

  private calculateMetrics(): void {
    // Calculate progress based on VAT CPD hours from API
    this.cpdProgress = Math.min((this.vatCpdFromApi / this.requiredCpdHours) * 100, 100);
  }

  /**
   * Load VAT CPD hours from API
   * Only count verified hours from the last 3 years
   */
  private loadVatCpdHours(): void {
    if (!this.currentUser?.id) {
      console.warn('⚠️ No user ID available for loading CPD hours');
      return;
    }

    this.isLoadingCpd = true;
    const apiUrl = `${environment.apiUrl}/cpd-hours/user/${this.currentUser.id}`;
    console.log('📚 Loading all CPD hours from:', apiUrl);

    // Calculate date 3 years ago from now
    const threeYearsAgo = new Date();
    threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);
    console.log('📅 Filtering CPD hours from:', threeYearsAgo.toISOString());

    this.http.get<any[]>(apiUrl).subscribe({
      next: (records) => {
        console.log('✅ All CPD hours loaded:', records);

        // Store all CPD hours records for checking registered courses
        this.cpdHoursRecords = records;

        // Filter only verified hours from the last 3 years (based on enrolledAt)
        const filteredRecords = records.filter(record => {
          if (!record.enrolledAt) {
            console.warn('⚠️ Record missing enrolledAt:', record.id);
            return false;
          }
          const enrolledDate = new Date(record.enrolledAt);
          const isVerified = record.isVerified === true;
          const isWithin3Years = enrolledDate >= threeYearsAgo;

          if (isVerified && isWithin3Years) {
            console.log('✅ Including record:', {
              id: record.id,
              hoursStudied: record.hoursStudied,
              enrolledAt: record.enrolledAt,
              isVerified: record.isVerified
            });
          }

          return isVerified && isWithin3Years;
        });

        console.log('✅ Filtered verified records from last 3 years (by enrolledAt):', filteredRecords);
        console.log('✅ Total filtered records count:', filteredRecords.length);

        // Calculate total hours
        this.vatCpdFromApi = filteredRecords.reduce((total, record) => {
          const hours = parseFloat(record.hoursStudied) || 0;
          console.log(`  Adding ${hours} hours from record ${record.id}`);
          return total + hours;
        }, 0);

        console.log('✅ Total verified CPD hours (last 3 years by enrolledAt):', this.vatCpdFromApi);

        // Calculate total course count (all records, not just 5 years)
        const uniqueCourseIds = new Set<string>();
        records.forEach(record => {
          if (record.courseId) {
            uniqueCourseIds.add(record.courseId);
          }
        });
        this.totalCourseCount = uniqueCourseIds.size;
        console.log('✅ Total unique courses:', this.totalCourseCount);

        this.calculateMetrics();
        this.isLoadingCpd = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('❌ Error loading VAT CPD hours:', error);
        this.vatCpdFromApi = 0;
        this.totalCourseCount = 0;
        this.isLoadingCpd = false;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Load VAT Member Card and Valuer Card data from user-information API
   */
  private loadVatMemberCard(userId: string): void {
    this.isLoadingVatCard = true;
    this.isLoadingValuerCard = true;
    const apiUrl = `${environment.apiUrl}/user-information/user/${userId}`;
    console.log('🎫 Loading VAT Member Card and Valuer Card from:', apiUrl);

    this.http.get<any>(apiUrl).subscribe({
      next: (response) => {
        console.log('✅ Card data loaded:', response);

        // VAT Member Card data
        this.vatMemberCardNumber = response.vatId || null;
        this.vatMemberCardExpiry = response.vatExpired || null;
        this.vatType = response.vatType || null;
        this.isLoadingVatCard = false;

        // Valuer Card data
        this.valuerCardNumber = response.valuerId || null;
        this.valuerCardExpiry = response.valuerExpired || null;
        this.valuerType = response.valuerType || null;
        this.isLoadingValuerCard = false;

        // User Status
        this.userStatusName = response.userStatus?.name || null;
        this.userStatusId = response.userStatusId || null;
        console.log('✅ User status loaded:', this.userStatusName, 'ID:', this.userStatusId);

        // Company data
        this.companyName = response.company?.organizationName || null;
        console.log('✅ Company name loaded:', this.companyName);

        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('❌ Error loading card data:', error);

        // VAT Member Card data
        this.vatMemberCardNumber = null;
        this.vatMemberCardExpiry = null;
        this.vatType = null;
        this.isLoadingVatCard = false;

        // Valuer Card data
        this.valuerCardNumber = null;
        this.valuerCardExpiry = null;
        this.valuerType = null;
        this.isLoadingValuerCard = false;

        // User Status
        this.userStatusName = null;
        this.userStatusId = null;

        // Company data
        this.companyName = null;

        this.cdr.detectChanges();
      }
    });
  }

  private checkLicenseStatus(): void {
    if (this.userDetails?.userInformation) {
      const now = new Date();

      // Check VAT expiry
      if (this.userDetails.userInformation.vatExpired) {
        const vatExpiry = new Date(this.userDetails.userInformation.vatExpired);
        const vatDiffTime = vatExpiry.getTime() - now.getTime();
        this.vatExpiringDays = Math.ceil(vatDiffTime / (1000 * 60 * 60 * 24));
      }

      // Check Valuer expiry
      if (this.userDetails.userInformation.valuerExpired) {
        const valuerExpiry = new Date(this.userDetails.userInformation.valuerExpired);
        const valuerDiffTime = valuerExpiry.getTime() - now.getTime();
        this.valuerExpiringDays = Math.ceil(valuerDiffTime / (1000 * 60 * 60 * 24));
      }
    }
  }

  // Getters for template
  get userName(): string {
    if (this.userDetails?.userInformation) {
      const firstName = this.userDetails.userInformation.firstName || '';
      const lastName = this.userDetails.userInformation.lastName || '';

      return `${firstName} ${lastName}`.trim() || this.currentUser?.cid || 'ผู้ใช้งาน';
    }
    return this.currentUser?.cid || 'ผู้ใช้งาน';
  }

  get totalCpdHours(): number {
    // Total CPD is now just VAT CPD from API
    return this.vatCpdFromApi;
  }

  get vatCpdHours(): number {
    return this.vatCpdFromApi;
  }

  get remainingCpdHours(): number {
    return Math.max(this.requiredCpdHours - this.totalCpdHours, 0);
  }

  get organization(): string {
    // Display company name if available, otherwise fallback to workplaceName or organization
    if (this.companyName) {
      return this.companyName;
    }
    return this.userDetails?.userInformation?.workplaceName ||
      this.userDetails?.userInformation?.organization ||
      'ไม่ได้ระบุ';
  }

  get phoneNumber(): string {
    return this.userDetails?.userInformation?.phoneNumber || '-';
  }

  get userRole(): string {
    return this.userDetails?.userRole?.role?.name || 'ผู้ใช้งาน';
  }

  get userRank(): string {
    return this.userDetails?.userRank?.rank?.name || '-';
  }

  get vatId(): string {
    return this.userDetails?.userInformation?.vatId || '-';
  }

  get valuerId(): string {
    return this.userDetails?.userInformation?.valuerId || '-';
  }

  get userProfileImage(): string | null {
    return this.userDetails?.userInformation?.profileImage || 
           this.userDetails?.userInformation?.profilePicture || 
           null;
  }

  get isVatExpiringSoon(): boolean {
    // สมาชิกตลอดชีพไม่มีวันหมดอายุ จึงไม่ต้องเตือนใกล้หมดอายุ
    if (this.isLifetimeVatMember) {
      return false;
    }

    return this.vatExpiringDays !== null && this.vatExpiringDays <= 90 && this.vatExpiringDays > 0;
  }

  get isValuerExpiringSoon(): boolean {
    return this.valuerExpiringDays !== null && this.valuerExpiringDays <= 90 && this.valuerExpiringDays > 0;
  }

  get isVatExpired(): boolean {
    // สมาชิกตลอดชีพไม่มีวันหมดอายุ
    if (this.isLifetimeVatMember) {
      return false;
    }

    return this.vatExpiringDays !== null && this.vatExpiringDays <= 0;
  }

  /**
   * เรียงรายการคำขอจากใหม่ไปเก่า (รายการล่าสุดอยู่บนสุด)
   */
  private sortByNewestFirst<T extends { createdAt?: string }>(items: T[]): T[] {
    return [...(items || [])].sort((a, b) => {
      const timeA = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
      const timeB = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      return timeB - timeA;
    });
  }

  /**
   * คำขอต่ออายุแบบตลอดชีพหรือไม่
   */
  isLifetimeRenewalRequest(request: any): boolean {
    return isLifetimeMemberType(request?.membershipType);
  }

  /**
   * หัวข้อของระยะเวลาที่ขอต่ออายุ
   */
  getRenewPeriodLabel(request: any): string {
    return this.isLifetimeRenewalRequest(request) ? 'ประเภทการต่ออายุ:' : 'จำนวนปีที่ต้องการต่ออายุ:';
  }

  /**
   * ข้อความระยะเวลาที่ขอต่ออายุ (ตลอดชีพไม่มีจำนวนปี)
   */
  getRenewPeriodText(request: any): string {
    if (this.isLifetimeRenewalRequest(request)) {
      return LIFETIME_MEMBER_LABEL;
    }

    return `${request?.workExperienceYears || 0} ปี`;
  }

  /**
   * เป็นสมาชิกแบบตลอดชีพหรือไม่
   */
  get isLifetimeVatMember(): boolean {
    return isLifetimeMemberType(this.vatType);
  }

  /**
   * ข้อความวันหมดอายุบัตรสมาชิก (ตลอดชีพจะแสดงคำว่า "ตลอดชีพ")
   */
  get vatMemberCardExpiryText(): string {
    return formatMemberExpiry(this.vatType, this.vatMemberCardExpiry, date => this.formatDate(date));
  }

  get isValuerExpired(): boolean {
    return this.valuerExpiringDays !== null && this.valuerExpiringDays <= 0;
  }

  /**
   * Check if active courses section should be shown
   * Hide if userStatusId is 2 (Pending) or 3 (Rejected)
   */
  get shouldShowActiveCourses(): boolean {
    // Show courses only if userStatusId is 1 (Approved) or null/undefined
    return this.userStatusId === null || this.userStatusId === 1;
  }

  get userStatusClass(): string {
    if (!this.userStatusName) {
      return this.userDetails?.isActive ? 'status-active' : 'status-inactive';
    }

    // Map status names to CSS classes
    const statusName = this.userStatusName.toLowerCase();

    if (statusName.includes('รอ approve') || statusName.includes('รออนุมัติ')) {
      return 'status-pending';
    } else if (statusName.includes('ไม่ผ่าน') || statusName.includes('ปฏิเสธ')) {
      return 'status-rejected';
    } else if (statusName.includes('approved') || statusName.includes('อนุมัติ')) {
      return 'status-approved';
    }
    // Fallback to active/inactive based on isActive flag
    return this.userDetails?.isActive ? 'status-active' : 'status-inactive';
  }

  // Actions
  onLogout(): void {
    this.authService.logout();
  }

  refreshData(): void {
    this.isLoading = true;
    this.error = null;

    if (this.currentUser) {
      this.loadUserDetails(this.currentUser.id);
      this.loadVatCpdHours();
      this.loadVatMemberCard(this.currentUser.id);
      this.loadActiveCourses();
      this.loadActiveExams();
      this.loadMemberRequests();
      this.loadMemberRenewalRequests();
      this.loadAppraiserRequests();
      this.loadAppraiserRenewalRequests();
      this.loadUserExams();
    } else {
      this.loadUserData();
    }
  }

  openProfileModal(): void {
    console.log('Opening profile modal');
    this.showProfileModal = true;
  }

  closeProfileModal(): void {
    console.log('Closing profile modal');
    this.showProfileModal = false;
  }

  formatDate(dateString: string | undefined): string {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('th-TH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  }

  openRegistrationForm(): void {
    this.modalService.open(FormRegisNewMemberComponent, {
      size: 'xl',
      centered: true,
      scrollable: true
    });
  }

  openRenewMemberForm(): void {
    const modalRef = this.modalService.open(FormRenewMemberComponent, {
      size: 'xl',
      centered: true,
      scrollable: true
    });
    modalRef.componentInstance.userId = this.currentUser?.id ?? '';
  }

  openAppraiserRegistrationForm(): void {
    this.modalService.open(FormRegisAppraiserComponent, {
      size: 'xl',
      centered: true,
      scrollable: true
    });
  }

  openAppraiserRenewalForm(): void {
    this.modalService.open(FormRenewAppraiserComponent, {
      size: 'xl',
      centered: true,
      scrollable: true
    });
  }

  /**
   * Navigate to edit user information page
   */
  navigateToEditUserInfo(): void {
    console.log('Navigating to edit user information page');
    this.router.navigate(['/edit-user-information']);
  }

  /**
   * Navigate to change password page
   */
  navigateToChangePassword(): void {
    console.log('Navigating to change password page');
    this.router.navigate(['/change-password']);
  }

  /**
   * Navigate to user course list page
   * @param filter - Optional filter mode: 'all' or '3years'
   */
  viewCourseList(filter: 'all' | '3years' = 'all'): void {
    console.log('Navigating to user course list page with filter:', filter);
    this.router.navigate(['/user/course-list'], {
      queryParams: { filter: filter }
    });
  }

  /**
   * Download receipt by navigating to renew-slip page
   * @param recordId - The ID of the request record
   * @param type - The type of request ('member-card' or 'renewal')
   */
  downloadReceipt(recordId: string, type: string): void {
    console.log('Navigating to receipt page:', recordId, type);
    this.router.navigate([`/user/renew-slip/${recordId}`], {
      queryParams: { type: type }
    });
  }

  /**
   * Navigate to user exam list page
   */
  viewExamList(): void {
    console.log('Navigating to user exam list page');
    this.router.navigate(['/user/exam-list']);
  }

  /**
   * Load active courses from API
   * Filter courses with courseStatusId = 2 (เปิดรับสมัคร)
   */
  private loadActiveCourses(): void {
    this.isLoadingActiveCourses = true;
    const apiUrl = `${environment.apiUrl}/courses`;
    console.log('📚 Loading courses from:', apiUrl);

    this.http.get<any[]>(apiUrl).subscribe({
      next: (courses) => {
        console.log('✅ All courses loaded:', courses);
        // Filter courses with courseStatusId = 2 (เปิดรับสมัคร only)
        this.activeCourses = courses
          .filter(course => course.courseStatusId === 2)
          .sort((a, b) => {
            // Sort by createdAt (start date) from latest to oldest
            const dateA = new Date(a.createdAt).getTime();
            const dateB = new Date(b.createdAt).getTime();
            return dateB - dateA; // Descending order (latest first)
          });
        console.log('✅ Filtered and sorted active courses (courseStatusId = 2, latest first):', this.activeCourses);
        this.isLoadingActiveCourses = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('❌ Error loading courses:', error);
        this.activeCourses = [];
        this.isLoadingActiveCourses = false;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Get enrolled count for a course
   */
  getEnrolledCount(course: any): number {
    return course.userCourses?.length || 0;
  }

  /**
   * Format price with Thai Baht
   */
  formatPrice(price: string | number | null | undefined): string {
    if (price == null) return '0.00';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return '0.00';
    return numPrice.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /**
   * Get level badge class
   */
  getLevelBadgeClass(level: string): string {
    const levelMap: { [key: string]: string } = {
      'beginner': 'badge-success',
      'intermediate': 'badge-warning',
      'advanced': 'badge-danger'
    };
    return levelMap[level] || 'badge-secondary';
  }

  /**
   * Get level display text in Thai
   */
  getLevelDisplayText(level: string): string {
    const levelMap: { [key: string]: string } = {
      'beginner': 'พื้นฐาน',
      'intermediate': 'ปานกลาง',
      'advanced': 'ขั้นสูง'
    };
    return levelMap[level] || level;
  }

  /**
   * Format course date to Thai format
   */
  formatCourseDate(dateString: string | null): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('th-TH', {
        // Course dates are naive wall-clock tagged UTC; format in UTC to avoid
        // the +7h (ICT) shift that rolls evening times to the next calendar day.
        timeZone: 'UTC',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  }

  /**
   * Check if user has already registered for a course
   * @param courseId - The course ID to check
   * @returns true if user has registered, false otherwise
   */
  isUserRegisteredForCourse(courseId: string): boolean {
    return this.cpdHoursRecords.some(record => record.courseId === courseId);
  }

  /**
   * Handle course card click - navigate to course list if already registered, otherwise to registration page
   * @param courseId - The course ID
   */
  handleCourseCardClick(courseId: string): void {
    if (this.isUserRegisteredForCourse(courseId)) {
      console.log('User already registered for course:', courseId, '- Navigating to course list with 3years filter');
      this.viewCourseList('3years');
    } else {
      console.log('User not registered for course:', courseId, '- Navigating to course register page');
      this.navigateToCourseRegister(courseId);
    }
  }

  /**
   * Navigate to course register page
   */
  navigateToCourseRegister(courseId: string): void {
    console.log('Navigating to course registration page for course:', courseId);
    this.router.navigate(['/user/course-register', courseId]);
  }

  /**
   * Switch between courses and exams tabs
   */
  switchTab(tab: 'courses' | 'exams'): void {
    this.activeTab = tab;
    this.cdr.detectChanges();
  }

  /**
   * Load active exams from API
   * Filter exams with examStatusId = 1 or 2
   */
  private loadActiveExams(): void {
    this.isLoadingActiveExams = true;
    const apiUrl = `${environment.apiUrl}/exams`;
    console.log('📝 Loading exams from:', apiUrl);

    this.http.get<any[]>(apiUrl).subscribe({
      next: (exams) => {
        console.log('✅ All exams loaded:', exams);
        // Filter exams with examStatusId = 1 or 2
        this.activeExams = exams.filter(exam =>
          exam.examStatusId === 1 || exam.examStatusId === 2
        );
        console.log('✅ Filtered active exams (status 1 or 2):', this.activeExams);
        this.isLoadingActiveExams = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('❌ Error loading exams:', error);
        this.activeExams = [];
        this.isLoadingActiveExams = false;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Get enrolled count for an exam
   */
  getExamEnrolledCount(exam: any): number {
    return exam.regisSeat || 0;
  }

  /**
   * Get exam status name from examStatus object
   */
  getExamStatusName(exam: any): string {
    return exam.examStatus?.name || '-';
  }

  /**
   * Get exam status badge class
   */
  getExamStatusBadgeClass(examStatusId: number): string {
    const classes: { [key: number]: string } = {
      1: 'badge-secondary',  // เตรียมเปิดสอบ
      2: 'badge-success',    // เปิดรับสมัคร
      3: 'badge-warning',    // ปิดรับสมัคร
      4: 'badge-danger'      // ยกเลิกการสอบ
    };
    return classes[examStatusId] || 'badge-secondary';
  }

  /**
   * Format exam date to Thai format
   */
  formatExamDate(dateString: string | null): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('th-TH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  }

  /**
   * ตรวจสอบว่า user สมัครสอบรายการนี้ไปแล้วหรือยัง (กันสมัครซ้ำ)
   */
  isUserRegisteredForExam(examId: string): boolean {
    return this.registeredExamIds.includes(examId);
  }

  /**
   * คลิกการ์ดการสอบ — ถ้าสมัครแล้วให้ไปหน้าติดตามสถานะ ไม่งั้นไปหน้าลงทะเบียน
   */
  handleExamCardClick(examId: string): void {
    if (this.isUserRegisteredForExam(examId)) {
      console.log('User already registered for exam:', examId, '- Navigating to exam status list');
      this.viewExamList();
    } else {
      this.navigateToExamRegister(examId);
    }
  }

  /**
   * Navigate to exam register page
   */
  navigateToExamRegister(examId: string): void {
    console.log('Navigating to exam registration page for exam:', examId);
    this.router.navigate(['/user/exam-register', examId]);
  }

  /**
   * Get course status name from courseStatus object
   */
  getCourseStatusName(course: any): string {
    return course.courseStatus?.name || '-';
  }

  /**
   * Get course status badge class
   */
  getCourseStatusBadgeClass(courseStatusId: number): string {
    const classes: { [key: number]: string } = {
      1: 'badge-secondary',  // เตรียมเปิดคอร์ส
      2: 'badge-success',    // เปิดรับสมัคร
      3: 'badge-warning',    // ปิดรับสมัคร
      4: 'badge-danger'      // ยกเลิกคอร์ส
    };
    return classes[courseStatusId] || 'badge-secondary';
  }

  /**
   * Get membership status badge class based on userStatusName
   */
  getMembershipStatusClass(): string {
    if (!this.userStatusName) {
      // Fallback to card expiry status if no user status
      if (this.vatMemberCardNumber && !this.isVatExpired) {
        return 'active';
      } else if (this.isVatExpired) {
        return 'expired';
      } else {
        return 'inactive';
      }
    }

    // Map status names to CSS classes based on request-regis-new-member-form statusOptions
    const statusName = this.userStatusName.toLowerCase();

    if (statusName.includes('รอ approve') || statusName.includes('รออนุมัติ') || statusName === 'รอตรวจเอกสาร') {
      return 'status-pending';  // Orange/Warning
    } else if (statusName === 'ผ่านการตรวจสอบ') {
      return 'status-verified';  // Blue/Info
    } else if (statusName === 'ดำเนินการออกบัตร') {
      return 'status-processing';  // Green/Success
    } else if (statusName === 'ดำเนินการเสร็จสิ้น') {
      return 'active';  // Green/Active
    } else if (statusName === 'ยกเลิก') {
      return 'status-cancelled';  // Red/Danger
    }

    // Default fallback
    return 'active';
  }

  /**
   * Get membership status text to display
   */
  getMembershipStatusText(): string {
    if (!this.userStatusName) {
      // Fallback to card expiry status if no user status
      if (this.vatMemberCardNumber && !this.isVatExpired) {
        return 'Active';
      } else if (this.isVatExpired) {
        return 'หมดอายุ';
      } else {
        return 'ยังไม่มีบัตร';
      }
    }

    // Return the actual status name from API
    return this.userStatusName;
  }

  /**
   * Load member requests from API
   */
  private loadMemberRequests(): void {
    if (!this.currentUser?.id) {
      console.warn('⚠️ No user ID available for loading member requests');
      return;
    }

    this.isLoadingMemberRequests = true;
    const apiUrl = `${environment.apiUrl}/form-request-new-member/user/${this.currentUser.id}`;
    console.log('📋 Loading member requests from:', apiUrl);

    this.http.get<any[]>(apiUrl).subscribe({
      next: (requests) => {
        console.log('✅ Member requests loaded:', requests);
        this.memberRequests = this.sortByNewestFirst(requests);

        // Initialize receipt types from DB values (read-only if already set)
        requests.forEach(req => {
          if (req.typeReceipt) {
            this.memberReceiptTypes.set(req.id, req.typeReceipt);
          }
        });

        this.isLoadingMemberRequests = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('❌ Error loading member requests:', error);
        this.memberRequests = [];
        this.isLoadingMemberRequests = false;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Format request date to Thai format
   */
  formatRequestDate(dateString: string): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('th-TH', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  }

  /**
   * Get request status badge class
   */
  getRequestStatusClass(status: string): string {
    const statusMap: { [key: string]: string } = {
      'รอตรวจเอกสาร': 'badge-secondary',
      'ผ่านการตรวจสอบ': 'badge-info',
      'ดำเนินการออกบัตร': 'badge-success',
      'ดำเนินการเสร็จสิ้น': 'badge-warning',
      'ยกเลิก': 'badge-danger'
    };
    return statusMap[status] || 'badge-secondary';
  }

  /**
   * Get payment status badge class
   */
  getPaymentStatusClass(paymentStatus: string): string {
    const statusMap: { [key: string]: string } = {
      'ยังไม่ชำระ': 'badge-danger',
      'แจ้งชำระค่าธรรมเนียม': 'badge-warning',
      'ชำระแล้ว': 'badge-success'
    };
    return statusMap[paymentStatus] || 'badge-secondary';
  }

  /**
   * Check if payment status allows upload
   */
  canUploadPaymentSlip(paymentStatus: string): boolean {
    return paymentStatus === 'รอชำระเงิน' || paymentStatus === 'ไม่ผ่านการตรวจสอบ';
  }

  /**
   * Handle payment slip file selection
   */
  onPaymentSlipSelected(event: any, requestId: string): void {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      alert('⚠️ กรุณาอัปโหลดไฟล์รูปภาพเท่านั้น (JPG, PNG)');
      event.target.value = '';
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      alert('⚠️ ขนาดไฟล์ต้องไม่เกิน 5MB');
      event.target.value = '';
      return;
    }

    // Upload file
    this.uploadPaymentSlip(file, requestId);
  }

  /**
   * Upload payment slip to API
   */
  private uploadPaymentSlip(file: File, requestId: string): void {
    this.uploadingSlips[requestId] = true;

    const formData = new FormData();
    formData.append('file', file);

    const apiUrl = `${environment.apiUrl}/form-request-new-member/${requestId}/upload-payment-slip`;
    console.log('📤 Uploading payment slip to:', apiUrl);

    this.http.post(apiUrl, formData).subscribe({
      next: (response: any) => {
        console.log('✅ Payment slip uploaded successfully:', response);
        this.uploadingSlips[requestId] = false;
        this.uploadedSlips[requestId] = file.name;

        // Show success message
        alert('✅ อัปโหลดสลิปการชำระเงินสำเร็จ');

        // Refresh member requests to get updated data
        this.loadMemberRequests();
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('❌ Error uploading payment slip:', error);
        this.uploadingSlips[requestId] = false;

        let errorMessage = 'เกิดข้อผิดพลาดในการอัปโหลดสลิป';
        if (error.status === 404) {
          errorMessage = 'ไม่พบรายการคำขอนี้';
        } else if (error.status === 400) {
          errorMessage = error.error?.message || 'ข้อมูลไม่ถูกต้อง';
        } else if (error.status === 0) {
          errorMessage = 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้';
        }

        alert(`❌ ${errorMessage}`);
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Check if upload is in progress for a request
   */
  isUploadingSlip(requestId: string): boolean {
    return this.uploadingSlips[requestId] || false;
  }

  /**
   * Open payment slip preview in new tab
   */
  openPaymentSlipPreview(paymentSlipUrl: string): void {
    if (paymentSlipUrl) {
      window.open(paymentSlipUrl, '_blank');
    }
  }

  /**
   * Load member renewal requests from API
   */
  private loadMemberRenewalRequests(): void {
    if (!this.currentUser?.id) {
      console.warn('⚠️ No user ID available for loading member renewal requests');
      return;
    }

    this.isLoadingMemberRenewalRequests = true;
    const apiUrl = `${environment.apiUrl}/form-renew-member/user/${this.currentUser.id}`;
    console.log('📋 Loading member renewal requests from:', apiUrl);

    this.http.get<any[]>(apiUrl).subscribe({
      next: (requests) => {
        console.log('✅ Member renewal requests loaded:', requests);
        this.memberRenewalRequests = this.sortByNewestFirst(requests);

        // Initialize receipt types from DB values (read-only if already set)
        requests.forEach((req: any) => {
          if (req.typeReceipt) {
            this.renewalReceiptTypes.set(req.id, req.typeReceipt);
          }
        });

        this.isLoadingMemberRenewalRequests = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('❌ Error loading member renewal requests:', error);
        this.memberRenewalRequests = [];
        this.isLoadingMemberRenewalRequests = false;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Upload payment slip for renewal request
   */
  onRenewalPaymentSlipSelected(event: any, requestId: string): void {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      alert('⚠️ กรุณาอัปโหลดไฟล์รูปภาพเท่านั้น (JPG, PNG)');
      event.target.value = '';
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      alert('⚠️ ขนาดไฟล์ต้องไม่เกิน 5MB');
      event.target.value = '';
      return;
    }

    // Upload file
    this.uploadRenewalPaymentSlip(file, requestId);
  }

  /**
   * Upload payment slip for renewal request to API
   */
  private uploadRenewalPaymentSlip(file: File, requestId: string): void {
    this.uploadingSlips[requestId] = true;

    const formData = new FormData();
    formData.append('file', file);

    const apiUrl = `${environment.apiUrl}/form-renew-member/${requestId}/upload-payment-slip`;
    console.log('📤 Uploading renewal payment slip to:', apiUrl);

    this.http.patch(apiUrl, formData).subscribe({
      next: (response: any) => {
        console.log('✅ Renewal payment slip uploaded successfully:', response);
        this.uploadingSlips[requestId] = false;
        this.uploadedSlips[requestId] = file.name;

        // Show success message
        alert('✅ อัปโหลดสลิปการชำระเงินสำเร็จ');

        // Refresh member renewal requests to get updated data
        this.loadMemberRenewalRequests();
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('❌ Error uploading renewal payment slip:', error);
        this.uploadingSlips[requestId] = false;

        let errorMessage = 'เกิดข้อผิดพลาดในการอัปโหลดสลิป';
        if (error.status === 404) {
          errorMessage = 'ไม่พบรายการคำขอนี้';
        } else if (error.status === 400) {
          errorMessage = error.error?.message || 'ข้อมูลไม่ถูกต้อง';
        } else if (error.status === 0) {
          errorMessage = 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้';
        }

        alert(`❌ ${errorMessage}`);
        this.cdr.detectChanges();
      }
    });
  }

  // ─── Member request: receipt type helpers ───────────────────────────────────

  getMemberReceiptType(requestId: string): string {
    return this.memberReceiptTypes.get(requestId) || '';
  }

  isMemberReceiptTypeReadOnly(requestId: string): boolean {
    const request = this.memberRequests.find(r => r.id === requestId);
    return !!(request && request.typeReceipt);
  }

  getMemberReceiptTypeLabel(type: string): string {
    if (type === 'individual') return 'ออกใบเสร็จรูปแบบส่วนบุคคล';
    if (type === 'company') return 'ออกใบเสร็จรูปแบบบริษัท';
    return '';
  }

  onMemberReceiptTypeChange(requestId: string, type: string): void {
    if (this.isMemberReceiptTypeReadOnly(requestId)) return;
    this.memberReceiptTypes.set(requestId, type);
    this.cdr.detectChanges();
  }

  // ─── Member request: transfer datetime helpers ───────────────────────────────

  getMemberTransferDateTime(requestId: string): string {
    return this.memberTransferDateTimes.get(requestId) || '';
  }

  onMemberTransferDateTimeChange(requestId: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.value) {
      const formatted = this.formatMemberTransferDateTime(new Date(input.value));
      this.memberTransferDateTimes.set(requestId, formatted);
      this.cdr.detectChanges();
    }
  }

  private formatMemberTransferDateTime(date: Date): string {
    const thaiMonths = [
      'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
      'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
    ];
    const day = date.getDate();
    const month = thaiMonths[date.getMonth()];
    const year = date.getFullYear() + 543;
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day} ${month} ${year} เวลา ${hours}:${minutes} น.`;
  }

  // ─── Member request: file selection + submit ─────────────────────────────────

  getSelectedMemberFileName(requestId: string): string | null {
    return this.selectedMemberPaymentSlips.get(requestId)?.fileName || null;
  }

  onMemberPaymentSlipSelected(event: any, requestId: string): void {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      alert('⚠️ กรุณาอัปโหลดไฟล์รูปภาพเท่านั้น (JPG, PNG)');
      event.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('⚠️ ขนาดไฟล์ต้องไม่เกิน 5MB');
      event.target.value = '';
      return;
    }

    this.selectedMemberPaymentSlips.set(requestId, { file, fileName: file.name });
    this.cdr.detectChanges();
  }

  submitMemberPaymentSlip(requestId: string): void {
    const slipData = this.selectedMemberPaymentSlips.get(requestId);
    if (!slipData) {
      alert('กรุณาเลือกไฟล์สลิปการชำระเงินก่อน');
      return;
    }

    const receiptType = this.memberReceiptTypes.get(requestId);
    if (!receiptType) {
      alert('กรุณาเลือกประเภทใบเสร็จก่อน');
      return;
    }

    const transferDateTime = this.memberTransferDateTimes.get(requestId);
    if (!transferDateTime) {
      alert('กรุณาระบุวันที่และเวลาที่โอนเงินก่อน');
      return;
    }

    if (!confirm('คุณต้องการส่งสลิปการชำระเงินใช่หรือไม่?')) return;

    this.uploadMemberPaymentSlipToApi(requestId, slipData.file, receiptType, transferDateTime);
  }

  private uploadMemberPaymentSlipToApi(
    requestId: string,
    file: File,
    typeReceipt: string,
    transferDateTime: string
  ): void {
    this.uploadingSlips[requestId] = true;

    const formData = new FormData();
    formData.append('file', file);

    const uploadUrl = `${environment.apiUrl}/form-request-new-member/${requestId}/upload-payment-slip`;
    console.log('📤 Uploading member payment slip to:', uploadUrl);

    this.http.post(uploadUrl, formData).subscribe({
      next: (response: any) => {
        console.log('✅ Member payment slip uploaded:', response);

        // PATCH typeReceipt and transferDateTime
        const patchUrl = `${environment.apiUrl}/form-request-new-member/${requestId}`;
        this.http.patch(patchUrl, { typeReceipt, transferDateTime }).subscribe({
          next: () => {
            this.uploadingSlips[requestId] = false;
            this.selectedMemberPaymentSlips.delete(requestId);
            this.memberTransferDateTimes.delete(requestId);
            alert('✅ ส่งสลิปการชำระเงินสำเร็จ! รอเจ้าหน้าที่ตรวจสอบ');
            this.loadMemberRequests();
            this.cdr.detectChanges();
          },
          error: () => {
            this.uploadingSlips[requestId] = false;
            alert('✅ อัปโหลดสลิปสำเร็จ (บันทึกข้อมูลเพิ่มเติมไม่สำเร็จ กรุณาแจ้งเจ้าหน้าที่)');
            this.loadMemberRequests();
            this.cdr.detectChanges();
          }
        });
      },
      error: (error) => {
        console.error('❌ Error uploading member payment slip:', error);
        this.uploadingSlips[requestId] = false;
        let msg = 'เกิดข้อผิดพลาดในการอัปโหลดสลิป';
        if (error.status === 0) msg = 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้';
        else if (error.status === 400) msg = error.error?.message || 'ข้อมูลไม่ถูกต้อง';
        else if (error.status === 404) msg = 'ไม่พบรายการคำขอนี้';
        alert(`❌ ${msg}`);
        this.cdr.detectChanges();
      }
    });
  }

  // ─── Member renewal: receipt type helpers ────────────────────────────────────

  getRenewalReceiptType(requestId: string): string {
    return this.renewalReceiptTypes.get(requestId) || '';
  }

  isRenewalReceiptTypeReadOnly(requestId: string): boolean {
    const request = this.memberRenewalRequests.find((r: any) => r.id === requestId);
    return !!(request && request.typeReceipt);
  }

  getRenewalReceiptTypeLabel(type: string): string {
    if (type === 'individual') return 'ออกใบเสร็จรูปแบบส่วนบุคคล';
    if (type === 'company') return 'ออกใบเสร็จรูปแบบบริษัท';
    return '';
  }

  onRenewalReceiptTypeChange(requestId: string, type: string): void {
    if (this.isRenewalReceiptTypeReadOnly(requestId)) return;
    this.renewalReceiptTypes.set(requestId, type);
    this.cdr.detectChanges();
  }

  // ─── Member renewal: transfer datetime helpers ───────────────────────────────

  getRenewalTransferDateTime(requestId: string): string {
    return this.renewalTransferDateTimes.get(requestId) || '';
  }

  onRenewalTransferDateTimeChange(requestId: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.value) {
      const formatted = this.formatMemberTransferDateTime(new Date(input.value));
      this.renewalTransferDateTimes.set(requestId, formatted);
      this.cdr.detectChanges();
    }
  }

  // ─── Member renewal: file selection + submit ──────────────────────────────────

  getSelectedRenewalFileName(requestId: string): string | null {
    return this.selectedRenewalPaymentSlips.get(requestId)?.fileName || null;
  }

  onRenewalPaymentSlipFileSelected(event: any, requestId: string): void {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      alert('⚠️ กรุณาอัปโหลดไฟล์รูปภาพเท่านั้น (JPG, PNG)');
      event.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('⚠️ ขนาดไฟล์ต้องไม่เกิน 5MB');
      event.target.value = '';
      return;
    }

    this.selectedRenewalPaymentSlips.set(requestId, { file, fileName: file.name });
    this.cdr.detectChanges();
  }

  submitRenewalPaymentSlip(requestId: string): void {
    const slipData = this.selectedRenewalPaymentSlips.get(requestId);
    if (!slipData) {
      alert('กรุณาเลือกไฟล์สลิปการชำระเงินก่อน');
      return;
    }

    const receiptType = this.renewalReceiptTypes.get(requestId);
    if (!receiptType) {
      alert('กรุณาเลือกประเภทใบเสร็จก่อน');
      return;
    }

    const transferDateTime = this.renewalTransferDateTimes.get(requestId);
    if (!transferDateTime) {
      alert('กรุณาระบุวันที่และเวลาที่โอนเงินก่อน');
      return;
    }

    if (!confirm('คุณต้องการส่งสลิปการชำระเงินใช่หรือไม่?')) return;

    this.uploadRenewalPaymentSlipToApi(requestId, slipData.file, receiptType, transferDateTime);
  }

  private uploadRenewalPaymentSlipToApi(
    requestId: string,
    file: File,
    typeReceipt: string,
    transferDateTime: string
  ): void {
    this.uploadingSlips[requestId] = true;

    const formData = new FormData();
    formData.append('file', file);

    const uploadUrl = `${environment.apiUrl}/form-renew-member/${requestId}/upload-payment-slip`;
    console.log('📤 Uploading renewal payment slip to:', uploadUrl);

    this.http.patch(uploadUrl, formData).subscribe({
      next: (response: any) => {
        console.log('✅ Renewal payment slip uploaded:', response);

        // PATCH typeReceipt and transferDateTime
        const patchUrl = `${environment.apiUrl}/form-renew-member/${requestId}`;
        this.http.patch(patchUrl, { typeReceipt, transferDateTime }).subscribe({
          next: () => {
            this.uploadingSlips[requestId] = false;
            this.selectedRenewalPaymentSlips.delete(requestId);
            this.renewalTransferDateTimes.delete(requestId);
            alert('✅ ส่งสลิปการชำระเงินสำเร็จ! รอเจ้าหน้าที่ตรวจสอบ');
            this.loadMemberRenewalRequests();
            this.cdr.detectChanges();
          },
          error: () => {
            this.uploadingSlips[requestId] = false;
            alert('✅ อัปโหลดสลิปสำเร็จ (บันทึกข้อมูลเพิ่มเติมไม่สำเร็จ กรุณาแจ้งเจ้าหน้าที่)');
            this.loadMemberRenewalRequests();
            this.cdr.detectChanges();
          }
        });
      },
      error: (error: any) => {
        console.error('❌ Error uploading renewal payment slip:', error);
        this.uploadingSlips[requestId] = false;
        let msg = 'เกิดข้อผิดพลาดในการอัปโหลดสลิป';
        if (error.status === 0) msg = 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้';
        else if (error.status === 400) msg = error.error?.message || 'ข้อมูลไม่ถูกต้อง';
        else if (error.status === 404) msg = 'ไม่พบรายการคำขอนี้';
        alert(`❌ ${msg}`);
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Load appraiser requests from API
   */
  private loadAppraiserRequests(): void {
    if (!this.currentUser?.id) {
      console.warn('⚠️ No user ID available for loading appraiser requests');
      return;
    }

    this.isLoadingAppraiserRequests = true;
    const apiUrl = `${environment.apiUrl}/form-regis-appraiser/user/${this.currentUser.id}`;
    console.log('📋 Loading appraiser requests from:', apiUrl);

    this.http.get<any[]>(apiUrl).subscribe({
      next: (requests) => {
        console.log('✅ Appraiser requests loaded:', requests);
        this.appraiserRequests = this.sortByNewestFirst(requests.map(req => ({
          ...req,
          valuationLogsCount: req.valuationLogs?.length || 0
        })));

        // Initialize receipt types from DB values (read-only if already set)
        requests.forEach((req: any) => {
          if (req.typeReceipt) {
            this.appraiserReceiptTypes.set(req.id, req.typeReceipt);
          }
        });

        this.isLoadingAppraiserRequests = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('❌ Error loading appraiser requests:', error);
        this.appraiserRequests = [];
        this.isLoadingAppraiserRequests = false;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Load appraiser renewal requests from API
   */
  private loadAppraiserRenewalRequests(): void {
    if (!this.currentUser?.id) {
      console.warn('⚠️ No user ID available for loading appraiser renewal requests');
      return;
    }

    this.isLoadingAppraiserRenewalRequests = true;
    const apiUrl = `${environment.apiUrl}/form-renew-appraiser/user/${this.currentUser.id}`;
    console.log('📋 Loading appraiser renewal requests from:', apiUrl);

    this.http.get<any[]>(apiUrl).subscribe({
      next: (requests) => {
        console.log('✅ Appraiser renewal requests loaded:', requests);
        this.appraiserRenewalRequests = this.sortByNewestFirst(requests.map(req => ({
          ...req,
          valuationLogsCount: req.valuationLogs?.length || 0
        })));

        // Initialize receipt types from DB values (read-only if already set)
        requests.forEach((req: any) => {
          if (req.typeReceipt) {
            this.appraiserRenewalReceiptTypes.set(req.id, req.typeReceipt);
          }
        });

        this.isLoadingAppraiserRenewalRequests = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('❌ Error loading appraiser renewal requests:', error);
        this.appraiserRenewalRequests = [];
        this.isLoadingAppraiserRenewalRequests = false;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Load user exams count from API
   */
  private loadUserExams(): void {
    if (!this.currentUser?.id) {
      console.warn('⚠️ No user ID available for loading user exams');
      return;
    }

    this.isLoadingUserExams = true;
    const apiUrl = `${environment.apiUrl}/exam-lists/user/${this.currentUser.id}`;
    console.log('📝 Loading user exams from:', apiUrl);

    this.http.get<any[]>(apiUrl).subscribe({
      next: (exams) => {
        console.log('✅ User exams loaded:', exams);
        this.userExamsCount = exams.length;
        // เก็บ examId ที่สมัครไปแล้ว เพื่อกันการสมัครสอบซ้ำ
        this.registeredExamIds = (exams || [])
          .map(e => e.examId || e.exam?.id)
          .filter((id): id is string => !!id);
        this.isLoadingUserExams = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('❌ Error loading user exams:', error);
        this.userExamsCount = 0;
        this.registeredExamIds = [];
        this.isLoadingUserExams = false;
        this.cdr.detectChanges();
      }
    });
  }

  // ─── Appraiser registration: receipt type helpers ────────────────────────────

  getAppraiserReceiptType(requestId: string): string {
    return this.appraiserReceiptTypes.get(requestId) || '';
  }

  isAppraiserReceiptTypeReadOnly(requestId: string): boolean {
    const request = this.appraiserRequests.find((r: any) => r.id === requestId);
    return !!(request && request.typeReceipt);
  }

  getAppraiserReceiptTypeLabel(type: string): string {
    if (type === 'individual') return 'ออกใบเสร็จรูปแบบส่วนบุคคล';
    if (type === 'company') return 'ออกใบเสร็จรูปแบบบริษัท';
    return '';
  }

  onAppraiserReceiptTypeChange(requestId: string, type: string): void {
    if (this.isAppraiserReceiptTypeReadOnly(requestId)) return;
    this.appraiserReceiptTypes.set(requestId, type);
    this.cdr.detectChanges();
  }

  // ─── Appraiser registration: transfer datetime helpers ───────────────────────

  getAppraiserTransferDateTime(requestId: string): string {
    return this.appraiserTransferDateTimes.get(requestId) || '';
  }

  onAppraiserTransferDateTimeChange(requestId: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.value) {
      const formatted = this.formatMemberTransferDateTime(new Date(input.value));
      this.appraiserTransferDateTimes.set(requestId, formatted);
      this.cdr.detectChanges();
    }
  }

  // ─── Appraiser registration: file selection + submit ─────────────────────────

  getSelectedAppraiserFileName(requestId: string): string | null {
    return this.selectedAppraiserPaymentSlips.get(requestId)?.fileName || null;
  }

  onAppraiserPaymentSlipSelected(event: any, requestId: string): void {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      alert('⚠️ กรุณาอัปโหลดไฟล์รูปภาพเท่านั้น (JPG, PNG)');
      event.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('⚠️ ขนาดไฟล์ต้องไม่เกิน 5MB');
      event.target.value = '';
      return;
    }

    this.selectedAppraiserPaymentSlips.set(requestId, { file, fileName: file.name });
    this.cdr.detectChanges();
  }

  submitAppraiserPaymentSlip(requestId: string): void {
    const slipData = this.selectedAppraiserPaymentSlips.get(requestId);
    if (!slipData) {
      alert('กรุณาเลือกไฟล์สลิปการชำระเงินก่อน');
      return;
    }

    const receiptType = this.appraiserReceiptTypes.get(requestId);
    if (!receiptType) {
      alert('กรุณาเลือกประเภทใบเสร็จก่อน');
      return;
    }

    const transferDateTime = this.appraiserTransferDateTimes.get(requestId);
    if (!transferDateTime) {
      alert('กรุณาระบุวันที่และเวลาที่โอนเงินก่อน');
      return;
    }

    if (!confirm('คุณต้องการส่งสลิปการชำระเงินใช่หรือไม่?')) return;

    this.uploadAppraiserPaymentSlipToApi(requestId, slipData.file, receiptType, transferDateTime);
  }

  private uploadAppraiserPaymentSlipToApi(
    requestId: string,
    file: File,
    typeReceipt: string,
    transferDateTime: string
  ): void {
    this.uploadingSlips[requestId] = true;

    const formData = new FormData();
    formData.append('file', file);

    const uploadUrl = `${environment.apiUrl}/form-regis-appraiser/${requestId}/upload-payment-slip`;
    console.log('📤 Uploading appraiser payment slip to:', uploadUrl);

    this.http.patch(uploadUrl, formData).subscribe({
      next: (response: any) => {
        console.log('✅ Appraiser payment slip uploaded:', response);

        // PATCH typeReceipt and transferDateTime
        const patchUrl = `${environment.apiUrl}/form-regis-appraiser/${requestId}`;
        this.http.patch(patchUrl, { typeReceipt, transferDateTime }).subscribe({
          next: () => {
            this.uploadingSlips[requestId] = false;
            this.selectedAppraiserPaymentSlips.delete(requestId);
            this.appraiserTransferDateTimes.delete(requestId);
            alert('✅ ส่งสลิปการชำระเงินสำเร็จ! รอเจ้าหน้าที่ตรวจสอบ');
            this.loadAppraiserRequests();
            this.cdr.detectChanges();
          },
          error: () => {
            this.uploadingSlips[requestId] = false;
            alert('✅ อัปโหลดสลิปสำเร็จ (บันทึกข้อมูลเพิ่มเติมไม่สำเร็จ กรุณาแจ้งเจ้าหน้าที่)');
            this.loadAppraiserRequests();
            this.cdr.detectChanges();
          }
        });
      },
      error: (error: any) => {
        console.error('❌ Error uploading appraiser payment slip:', error);
        this.uploadingSlips[requestId] = false;
        let msg = 'เกิดข้อผิดพลาดในการอัปโหลดสลิป';
        if (error.status === 0) msg = 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้';
        else if (error.status === 400) msg = error.error?.message || 'ข้อมูลไม่ถูกต้อง';
        else if (error.status === 404) msg = 'ไม่พบรายการคำขอนี้';
        alert(`❌ ${msg}`);
        this.cdr.detectChanges();
      }
    });
  }

  // ─── Appraiser renewal: receipt type helpers ─────────────────────────────────

  getAppraiserRenewalReceiptType(requestId: string): string {
    return this.appraiserRenewalReceiptTypes.get(requestId) || '';
  }

  isAppraiserRenewalReceiptTypeReadOnly(requestId: string): boolean {
    const request = this.appraiserRenewalRequests.find((r: any) => r.id === requestId);
    return !!(request && request.typeReceipt);
  }

  getAppraiserRenewalReceiptTypeLabel(type: string): string {
    if (type === 'individual') return 'ออกใบเสร็จรูปแบบส่วนบุคคล';
    if (type === 'company') return 'ออกใบเสร็จรูปแบบบริษัท';
    return '';
  }

  onAppraiserRenewalReceiptTypeChange(requestId: string, type: string): void {
    if (this.isAppraiserRenewalReceiptTypeReadOnly(requestId)) return;
    this.appraiserRenewalReceiptTypes.set(requestId, type);
    this.cdr.detectChanges();
  }

  // ─── Appraiser renewal: transfer datetime helpers ─────────────────────────────

  getAppraiserRenewalTransferDateTime(requestId: string): string {
    return this.appraiserRenewalTransferDateTimes.get(requestId) || '';
  }

  onAppraiserRenewalTransferDateTimeChange(requestId: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.value) {
      const formatted = this.formatMemberTransferDateTime(new Date(input.value));
      this.appraiserRenewalTransferDateTimes.set(requestId, formatted);
      this.cdr.detectChanges();
    }
  }

  // ─── Appraiser renewal: file selection + submit ───────────────────────────────

  getSelectedAppraiserRenewalFileName(requestId: string): string | null {
    return this.selectedAppraiserRenewalPaymentSlips.get(requestId)?.fileName || null;
  }

  onAppraiserRenewalPaymentSlipSelected(event: any, requestId: string): void {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      alert('⚠️ กรุณาอัปโหลดไฟล์รูปภาพเท่านั้น (JPG, PNG)');
      event.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('⚠️ ขนาดไฟล์ต้องไม่เกิน 5MB');
      event.target.value = '';
      return;
    }

    this.selectedAppraiserRenewalPaymentSlips.set(requestId, { file, fileName: file.name });
    this.cdr.detectChanges();
  }

  submitAppraiserRenewalPaymentSlip(requestId: string): void {
    const slipData = this.selectedAppraiserRenewalPaymentSlips.get(requestId);
    if (!slipData) {
      alert('กรุณาเลือกไฟล์สลิปการชำระเงินก่อน');
      return;
    }

    const receiptType = this.appraiserRenewalReceiptTypes.get(requestId);
    if (!receiptType) {
      alert('กรุณาเลือกประเภทใบเสร็จก่อน');
      return;
    }

    const transferDateTime = this.appraiserRenewalTransferDateTimes.get(requestId);
    if (!transferDateTime) {
      alert('กรุณาระบุวันที่และเวลาที่โอนเงินก่อน');
      return;
    }

    if (!confirm('คุณต้องการส่งสลิปการชำระเงินใช่หรือไม่?')) return;

    this.uploadAppraiserRenewalPaymentSlipToApi(requestId, slipData.file, receiptType, transferDateTime);
  }

  private uploadAppraiserRenewalPaymentSlipToApi(
    requestId: string,
    file: File,
    typeReceipt: string,
    transferDateTime: string
  ): void {
    this.uploadingSlips[requestId] = true;

    const formData = new FormData();
    formData.append('file', file);

    const uploadUrl = `${environment.apiUrl}/form-renew-appraiser/${requestId}/upload-payment-slip`;
    console.log('📤 Uploading appraiser renewal payment slip to:', uploadUrl);

    this.http.patch(uploadUrl, formData).subscribe({
      next: (response: any) => {
        console.log('✅ Appraiser renewal payment slip uploaded:', response);

        // PATCH typeReceipt and transferDateTime
        const patchUrl = `${environment.apiUrl}/form-renew-appraiser/${requestId}`;
        this.http.patch(patchUrl, { typeReceipt, transferDateTime }).subscribe({
          next: () => {
            this.uploadingSlips[requestId] = false;
            this.selectedAppraiserRenewalPaymentSlips.delete(requestId);
            this.appraiserRenewalTransferDateTimes.delete(requestId);
            alert('✅ ส่งสลิปการชำระเงินสำเร็จ! รอเจ้าหน้าที่ตรวจสอบ');
            this.loadAppraiserRenewalRequests();
            this.cdr.detectChanges();
          },
          error: () => {
            this.uploadingSlips[requestId] = false;
            alert('✅ อัปโหลดสลิปสำเร็จ (บันทึกข้อมูลเพิ่มเติมไม่สำเร็จ กรุณาแจ้งเจ้าหน้าที่)');
            this.loadAppraiserRenewalRequests();
            this.cdr.detectChanges();
          }
        });
      },
      error: (error: any) => {
        console.error('❌ Error uploading appraiser renewal payment slip:', error);
        this.uploadingSlips[requestId] = false;
        let msg = 'เกิดข้อผิดพลาดในการอัปโหลดสลิป';
        if (error.status === 0) msg = 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้';
        else if (error.status === 400) msg = error.error?.message || 'ข้อมูลไม่ถูกต้อง';
        else if (error.status === 404) msg = 'ไม่พบรายการคำขอนี้';
        alert(`❌ ${msg}`);
        this.cdr.detectChanges();
      }
    });
  }

}
